import React, { useContext } from 'react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import axios from 'utils/api/axios'
import AxiosUtils from 'utils/api/axiosUtils'
import { PN_TYPES } from 'constants/constants'
import Modal from '@airbus/components-react/components/Modal'
import FormInput from 'components/FormFields/FormInput'
import { Message, MessageSeverity } from 'constants/message'
import { HTTP_CODES } from 'constants/constants'
import {
  Button,
  Table,
  TableHead,
  TableBody,
  TableRow,
  Inline,
  Container,
  useColorMode,
} from '@airbus/components-react/components'
import GoToTop from 'components/goToTop'
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form'
import URL_APIS from 'utils/api/urlApis'
import tools from 'utils/helpers'
import PageHeader from 'components/pageHeader'
import { PnSearch } from 'types/pn'
import MessageContext from 'context/MessageContext'
import AuthContext from 'context/AuthContext'
import { Lang } from 'context/LangContext'
import LightDarkContext from 'context/LightDarkModeContext'
import ResponsiveTableCell from 'components/responsiveTableCell'
import useMediaQuery from '@airbus/components-react/lib/hooks/useMediaQuery'

const ID_SCROLL_COMPONENT_FOR_GOTOTOP = 'modalPnGoToTop'

type Props = {
  formTitle: string
  lang: Lang
  isOpened: boolean
  avnumap?: string
  onCloseModal: () => void
  onPnSelected: (pn: PnSearch) => void
  noPnFilter: boolean
}

type Inputs = {
  pnContains: string
  designationContains: string
}

const urlPn = URL_APIS.URL_COMMON_PN_SEARCH_GET_PARAMETERIZED

const styleModal = {
  position: 'absolute',
  transform: 'translate(20%, 50%)',
  width: '70%',
  height: '50%',
  border: '2px solid #000',
  boxShadow: 24,
  overflowY: 'scroll',
  scrollbarWidth: 'none',
}

const ModalPn = ({ formTitle, lang, isOpened, avnumap, onCloseModal, onPnSelected, noPnFilter = false }: Props) => {
  const { t } = useTranslation()

  const [data, setData] = useState<PnSearch>([])
  const [isLoading, setIsLoading] = useState(false)
  const { handleDisplayMessage } = useContext(MessageContext)
  const { handleRequestLogin } = useContext(AuthContext)

  const methods = useForm<Inputs>({
    defaultValues: { pnContains: '', designationContains: '' },
  })

  const { handleSubmit, resetField } = methods

  const handleSearchSubmit: SubmitHandler<Inputs> = async (data) => {
    let pnTypesFilter: string[] = []

    if (!noPnFilter) {
      pnTypesFilter = [PN_TYPES.SERIALISE_REPARABLE, PN_TYPES.SERIALISE_NON_REPARABLE]
    }
    try {
      console.debug(
        'Page <' +
          formTitle +
          '> :: Recherche <PN> :: called URL = ' +
          encodeURI(
            tools.dformat(
              urlPn,
              data.pnContains,
              data.designationContains,
              pnTypesFilter.map((pnType) => '&pnTypes=' + pnType).join(''),
              avnumap ? avnumap : '',
            ),
          ) +
          "'",
      )
      setIsLoading(true)
      const headerCompleted = await AxiosUtils.getHeader(AxiosUtils.HEADERS.HEADER_READ)
      const response = await axios.get(
        encodeURI(
          tools.dformat(
            urlPn,
            data.pnContains && data.pnContains.toUpperCase(),
            data.designationContains,
            pnTypesFilter.map((pnType) => '&pnTypes=' + pnType).join(''),
            avnumap ? avnumap : '',
          ),
        ),
        {
          headers: headerCompleted,
          withCredentials: false,
        },
      )

      // Init widget locally
      console.debug('data retrieved <' + formTitle + '> :: Recherche <PN>')
      setData(response?.data)
    } catch (err: any) {
      console.debug('ERR <' + t('9500054') + '>: ' + err)
      if (err?.response?.status === HTTP_CODES.HTTP_CODE_SESSION_EXPIRED) {
        handleDisplayMessage(new Message(MessageSeverity.WARNING, t('9500054'), null, t('sessionExpired')))
        handleRequestLogin()
      } else if (err.response.status === HTTP_CODES.HTTP_CODE_NOT_FOUND) {
        handleDisplayMessage(new Message(MessageSeverity.ERROR, t('9500054'), null, t('9500173')))
      } else {
        handleDisplayMessage(
          new Message(
            MessageSeverity.ERROR,
            t('9500054'),
            err.response.data?.amasisCode,
            err.response.data?.description
              ? err.response.data?.description
              : err.response.data.title
                ? err.response.data.title
                : err.response.data.message
                  ? err.response.data.message
                  : err.response.data.error,
          ),
        )
      }
    } finally {
      setIsLoading(false)
    }
  }

  const clearForm = () => {
    resetField('pnContains')
    resetField('designationContains')
    setData([])
  }

  const handleCloseModal = () => {
    clearForm()
    onCloseModal()
  }

  const handlePnSelection = (pnSelected: PnSearch) => {
    clearForm()
    onPnSelected(pnSelected)
  }

  const { getBackgroundColor } = useContext(LightDarkContext)

  const {
    responsive: { breakpoint },
  } = useColorMode()

  const mqMaxMD = useMediaQuery(`(max-width: ${breakpoint.lg}px)`)

  return (
    <Modal
      open={isOpened}
      onClose={handleCloseModal}
      aria-labelledby='modal-modal-title'
      aria-describedby='modal-modal-description'
    >
      <div style={{ ...styleModal, backgroundColor: getBackgroundColor() }} id={ID_SCROLL_COMPONENT_FOR_GOTOTOP}>
        <>
          <PageHeader
            pageTitle={t('modal_search_pn')}
            modalEmbedded={true}
            modalOnClose={onCloseModal}
            style={{ top: 0 }}
            localLoading={isLoading}
          />

          <Container style={{ flex: 1 }} fixed>
            <div style={{ padding: mqMaxMD ? '0.125em' : '1em' }}>
              <FormProvider {...methods}>
                <form style={{ marginTop: '1em' }} onSubmit={handleSubmit(handleSearchSubmit)}>
                  <Inline spacing='2-x' style={{ width: '100%' }}>
                    <FormInput
                      name='pnContains'
                      rules={{ required: false }}
                      label={t('9500047')}
                      maxLength={40}
                      capitalize
                    />
                    <FormInput
                      name='designationContains'
                      rules={{ required: false }}
                      label={t('7000248')}
                      maxLength={60}
                      capitalize
                    />
                  </Inline>
                  <div style={{ width: '100%' }}>
                    <Inline spacing='1-x' style={{ margin: '10px 0' }}>
                      <Button variant='secondary' onClick={handleCloseModal}>
                        {t('8000569')}
                      </Button>
                      <Button variant='primary' type='submit' disabled={!methods.formState.isDirty || isLoading}>
                        {t('7001655')}
                      </Button>
                    </Inline>
                  </div>
                </form>
              </FormProvider>
              {data && data.length > 0 ? (
                <Table stickyHeader>
                  <TableHead>
                    <TableRow>
                      <ResponsiveTableCell>{t('9500047')}</ResponsiveTableCell>
                      <ResponsiveTableCell>{t('7000248')}</ResponsiveTableCell>
                      <ResponsiveTableCell>{t('1P00012')}</ResponsiveTableCell>
                      <ResponsiveTableCell>{t('7000956')}</ResponsiveTableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {data.map((row: PnSearch, i: number) => (
                      <TableRow key={'pnSearch_' + i} hover={true} onClick={() => handlePnSelection(row)}>
                        <ResponsiveTableCell>{row?.pn}</ResponsiveTableCell>
                        <ResponsiveTableCell>{row?.designation}</ResponsiveTableCell>
                        <ResponsiveTableCell>{row?.materialType}</ResponsiveTableCell>
                        <ResponsiveTableCell>{row?.type}</ResponsiveTableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              ) : (
                <div>{t('9500055')}</div>
              )}
            </div>
          </Container>
          <GoToTop idComponentForScroll={ID_SCROLL_COMPONENT_FOR_GOTOTOP} />
        </>
      </div>
    </Modal>
  )
}

export default ModalPn
